import {
  Glass,
  Dot,
  ScreenLabel,
  DisplayText,
  BodyText,
  BackNav,
  Flag,
} from "../../components/mobile/primitives";

/** WBC progression detail — hero value, sparkline chart, reference range, history table */
export function FlowWBCProgression() {
  const values = [
    {
      date: "Aug 2024",
      value: 6.2,
      flag: null,
      source: "Quest Diagnostics",
      confidence: 96,
      refLow: 4.5,
      refHigh: 11.0,
    },
    {
      date: "Dec 2024",
      value: 7.8,
      flag: null,
      source: "Quest Diagnostics",
      confidence: 98,
      refLow: 4.5,
      refHigh: 11.0,
    },
    {
      date: "Jun 2025",
      value: 9.1,
      flag: null,
      source: "LabCorp",
      confidence: 94,
      refLow: 4.5,
      refHigh: 11.0,
    },
    {
      date: "Oct 2025",
      value: 10.8,
      flag: "HIGH",
      source: "LabCorp",
      confidence: 97,
      refLow: 4.5,
      refHigh: 11.0,
    },
    {
      date: "Feb 2026",
      value: 11.4,
      flag: "HIGH",
      source: "Quest Diagnostics",
      confidence: 99,
      refLow: 4.5,
      refHigh: 11.0,
    },
  ];

  // Chart dimensions
  const chartW = 220;
  const chartH = 80;
  const padL = 4;
  const padR = 4;
  const padT = 8;
  const padB = 8;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  const vals = values.map((v) => v.value);
  const minV = Math.min(...vals) - 1;
  const maxV = Math.max(...vals) + 1;
  const range = maxV - minV;

  const refLow = 4.5;
  const refHigh = 11.0;
  const bandY1 = padT + plotH * (1 - (refHigh - minV) / range);
  const bandY2 = padT + plotH * (1 - (refLow - minV) / range);
  const bandH = bandY2 - bandY1;

  const points = values.map((v, i) => ({
    x: padL + (i / (values.length - 1)) * plotW,
    y: padT + plotH * (1 - (v.value - minV) / range),
    ...v,
  }));

  const pathD = points.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    return `${d} L ${p.x} ${p.y}`;
  }, "");

  const reversed = [...values].reverse();
  const expandedIndex = 0; // latest row is expanded

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Back header */}
      <BackNav label="Index" />

      {/* Hero section */}
      <div>
        <ScreenLabel style={{ marginBottom: 3 }}>Hematology</ScreenLabel>
        <DisplayText size={22}>White Blood Cells</DisplayText>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
          <DisplayText size={36} color="var(--color-kin-rose)">
            11.4
          </DisplayText>
          <BodyText size={12} color="var(--color-kin-muted)" weight={500}>
            ×10³/µL
          </BodyText>
          <Flag type="HIGH" />
        </div>
        <BodyText size={9.5} color="var(--color-kin-orange)" weight={600} style={{ marginTop: 3 }}>
          ↑ Increasing trend over 18 months
        </BodyText>
        <BodyText size={8.5} color="var(--color-kin-muted)" weight={400} style={{ marginTop: 1 }}>
          Ref: 4.5 – 11.0 ×10³/µL
        </BodyText>
      </div>

      {/* Chart card */}
      <Glass style={{ borderRadius: 18 }}>
        <div style={{ padding: "12px 10px 8px" }}>
          <ScreenLabel style={{ marginBottom: 8, paddingLeft: 4 }}>Trend</ScreenLabel>
          <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`}>
            <rect
              x={padL}
              y={bandY1}
              width={plotW}
              height={bandH}
              rx={3}
              fill="rgba(91,122,102,0.08)"
              stroke="rgba(91,122,102,0.15)"
              strokeWidth={0.5}
            />
            <text
              x={chartW - padR - 1}
              y={bandY1 - 2}
              textAnchor="end"
              fontSize={6}
              fill="var(--color-kin-muted)"
              fontFamily="Manrope, system-ui"
            >
              11.0
            </text>
            <text
              x={chartW - padR - 1}
              y={bandY2 + 8}
              textAnchor="end"
              fontSize={6}
              fill="var(--color-kin-muted)"
              fontFamily="Manrope, system-ui"
            >
              4.5
            </text>
            <path
              d={pathD}
              fill="none"
              stroke="var(--color-kin-green-soft)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {points.map((p, i) => (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={p.flag ? 4.5 : 3.5}
                  fill={p.flag ? "var(--color-kin-rose)" : "var(--color-kin-green-soft)"}
                  stroke="#fff"
                  strokeWidth={1.5}
                />
                <text
                  x={p.x}
                  y={chartH - 1}
                  textAnchor="middle"
                  fontSize={6}
                  fill="var(--color-kin-muted)"
                  fontFamily="Manrope, system-ui"
                >
                  {p.date.split(" ")[0]}
                </text>
              </g>
            ))}
          </svg>
        </div>
      </Glass>

      {/* History table */}
      <Glass style={{ borderRadius: 18 }}>
        <div style={{ padding: "12px 12px 4px" }}>
          <BodyText size={10.5} weight={700} style={{ marginBottom: 8 }}>
            History
          </BodyText>

          {/* Table header */}
          <div
            style={{
              display: "flex",
              padding: "4px 0",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <ScreenLabel style={{ flex: 2 }}>Date</ScreenLabel>
            <ScreenLabel style={{ flex: 2 }}>Value</ScreenLabel>
            <ScreenLabel style={{ width: 22, textAlign: "center" }}></ScreenLabel>
            <ScreenLabel style={{ flex: 3 }}>Source</ScreenLabel>
          </div>

          {reversed.map((v, i) => {
            const isExpanded = i === expandedIndex;
            return (
              <div key={v.date}>
                {/* Row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "7px 0",
                    borderBottom:
                      !isExpanded && i < reversed.length - 1
                        ? "1px solid rgba(0,0,0,0.04)"
                        : "none",
                    ...(isExpanded
                      ? {
                          background: "rgba(165,144,255,0.06)",
                          borderLeft: "2px solid var(--color-kin-lavender)",
                          paddingLeft: 6,
                          marginLeft: -4,
                          borderRadius: "4px 4px 0 0",
                        }
                      : {}),
                  }}
                >
                  <BodyText
                    size={9}
                    color="var(--color-kin-muted)"
                    weight={400}
                    style={{ flex: 2 }}
                  >
                    {v.date}
                  </BodyText>
                  <BodyText size={9.5} weight={500} style={{ flex: 2 }}>
                    {v.value}
                  </BodyText>
                  <div style={{ width: 22, display: "flex", justifyContent: "center" }}>
                    <Flag type={v.flag} />
                  </div>
                  <BodyText
                    size={8}
                    color="var(--color-kin-green-soft)"
                    weight={500}
                    style={{ flex: 3 }}
                  >
                    {v.source}
                  </BodyText>
                </div>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div
                    style={{
                      background: "rgba(165,144,255,0.06)",
                      borderLeft: "2px solid var(--color-kin-lavender)",
                      marginLeft: -4,
                      paddingLeft: 6,
                      paddingRight: 4,
                      paddingBottom: 8,
                      borderRadius: "0 0 4px 4px",
                      borderBottom: i < reversed.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none",
                    }}
                  >
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingTop: 2 }}>
                      {/* Reference range */}
                      <div
                        style={{
                          flex: "1 1 80px",
                          padding: "6px 8px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.5)",
                          border: "1px solid rgba(0,0,0,0.04)",
                        }}
                      >
                        <BodyText
                          size={7}
                          color="var(--color-kin-muted)"
                          weight={600}
                          style={{
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            marginBottom: 2,
                          }}
                        >
                          Ref range
                        </BodyText>
                        <BodyText size={9.5} weight={600}>
                          {v.refLow} – {v.refHigh}
                        </BodyText>
                        <BodyText size={8} color="var(--color-kin-muted)" weight={400}>
                          ×10³/µL
                        </BodyText>
                      </div>

                      {/* Confidence */}
                      <div
                        style={{
                          flex: "1 1 80px",
                          padding: "6px 8px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.5)",
                          border: "1px solid rgba(0,0,0,0.04)",
                        }}
                      >
                        <BodyText
                          size={7}
                          color="var(--color-kin-muted)"
                          weight={600}
                          style={{
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            marginBottom: 2,
                          }}
                        >
                          Confidence
                        </BodyText>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                          <BodyText size={9.5} weight={600}>
                            {v.confidence}%
                          </BodyText>
                        </div>
                        {/* Mini confidence bar */}
                        <div
                          style={{
                            height: 3,
                            borderRadius: 2,
                            background: "rgba(0,0,0,0.06)",
                            marginTop: 3,
                          }}
                        >
                          <div
                            style={{
                              width: `${v.confidence}%`,
                              height: "100%",
                              borderRadius: 2,
                              background: "var(--color-kin-green-soft)",
                            }}
                          />
                        </div>
                      </div>

                      {/* Delta from ref */}
                      <div
                        style={{
                          flex: "1 1 80px",
                          padding: "6px 8px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.5)",
                          border: "1px solid rgba(0,0,0,0.04)",
                        }}
                      >
                        <BodyText
                          size={7}
                          color="var(--color-kin-muted)"
                          weight={600}
                          style={{
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            marginBottom: 2,
                          }}
                        >
                          Above ref
                        </BodyText>
                        <BodyText size={9.5} weight={600} color="var(--color-kin-rose)">
                          +{(v.value - v.refHigh).toFixed(1)}
                        </BodyText>
                        <BodyText size={8} color="var(--color-kin-muted)" weight={400}>
                          ×10³/µL
                        </BodyText>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Glass>

      {/* Insight note */}
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 14,
          background: "rgba(255,248,225,0.6)",
          border: "1px solid rgba(240,184,0,0.22)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <Dot color="var(--color-kin-gold-dark)" size={5} />
          <BodyText size={9} color="var(--color-kin-gold-dark)" weight={700}>
            Trend note
          </BodyText>
        </div>
        <BodyText size={9} color="var(--color-kin-muted)" weight={400} style={{ lineHeight: 1.5 }}>
          WBC has been rising steadily since Aug 2024. The last two results are above the reference
          range. Tap a row to expand details.
        </BodyText>
      </div>
    </div>
  );
}
